import React from "react";
import { Navigate, Link } from "react-router-dom";
import { Printer, ArrowLeft, Check, Phone, MapPin, Globe, MessageCircle } from "lucide-react";
import { LOGOS, BRANCHES } from "@/lib/brand";
import { useAuth } from "@/contexts/AuthContext";

// Roll-fold tri-fold brochure on A4 landscape (297mm x 210mm)
// Right panel is slightly narrower so it tucks in when folded.
const TOTAL_W = 297;
const PAGE_H = 210;
const PANEL_RIGHT = 96; // tuck panel (narrower)
const PANEL_MID = 100;
const PANEL_LEFT = 101;

function PanelDivider({ left }) {
  return (
    <div
      className="absolute top-0 bottom-0 border-l border-dashed border-white/10 pointer-events-none print:hidden"
      style={{ left: `${left}mm` }}
    />
  );
}

/* =========================================================================
   INTÉRIEUR (les 3 pôles)
   Layout when unfolded, from left to right:
     [ PANEL LEFT  ] [ PANEL MIDDLE ] [ PANEL RIGHT ]
   With roll fold, the right panel tucks first, so for reading order:
     LEFT = Studio, MIDDLE = Sécurité, RIGHT = Propreté
   ========================================================================= */
function PanelPole({ branch, width }) {
  const b = branch;
  return (
    <div
      data-testid={`flyer-pole-${b.key}`}
      className="relative h-full bg-[#0a0a0a] text-white overflow-hidden"
      style={{ width: `${width}mm` }}
    >
      {/* Halo radial coloré */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(70% 50% at 50% 0%, ${b.accent}33, transparent 70%)`,
        }}
      />

      <div className="relative h-full flex flex-col" style={{ padding: "22mm 9mm 12mm 9mm" }}>
        {/* Logo */}
        <div
          className="self-center rounded-md overflow-hidden bg-white ring-1 ring-white/10"
          style={{ width: "44mm", height: "44mm", marginBottom: "7mm" }}
        >
          <img
            src={LOGOS[b.key]}
            alt={b.name}
            className="w-full h-full object-contain"
            style={{ padding: "2mm" }}
          />
        </div>

        {/* Titre */}
        <div className="text-center">
          <div style={{ fontSize: "2.4mm", letterSpacing: "0.3em", color: "#9ca3af", textTransform: "uppercase" }}>
            Pôle
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "8mm",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.05,
              marginTop: "1.5mm",
            }}
          >
            {b.name}
          </h2>
          <div style={{ color: b.accent, fontSize: "3mm", fontWeight: 500, marginTop: "2mm" }}>
            {b.tagline}
          </div>
        </div>

        {/* Description */}
        <p style={{ fontSize: "2.9mm", lineHeight: 1.55, color: "#d4d4d4", marginTop: "6mm", textAlign: "center" }}>
          {b.description}
        </p>

        {/* Services */}
        <div style={{ marginTop: "7mm" }}>
          <div
            style={{
              fontSize: "2.2mm",
              letterSpacing: "0.25em",
              color: "#9ca3af",
              textTransform: "uppercase",
              borderTop: "0.2mm solid rgba(255,255,255,0.15)",
              paddingTop: "3.5mm",
              marginBottom: "3.5mm",
              textAlign: "center",
            }}
          >
            Nos services
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {b.services.filter((s) => s !== "Autre").map((svc) => (
              <li
                key={svc}
                className="flex items-start"
                style={{ gap: "2.5mm", marginBottom: "2.4mm", fontSize: "2.7mm", lineHeight: 1.3 }}
              >
                <span
                  className="flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ width: "3.5mm", height: "3.5mm", backgroundColor: `${b.accent}22`, color: b.accent, marginTop: "0.3mm" }}
                >
                  <Check size={6} strokeWidth={3} />
                </span>
                <span style={{ color: "#e5e5e5" }}>{svc}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA bas */}
        <div className="mt-auto pt-[5mm] text-center">
          <div
            className="inline-block"
            style={{
              padding: "2.5mm 6mm",
              border: `0.3mm solid ${b.accent}`,
              borderRadius: "20mm",
              fontSize: "2.4mm",
              color: b.accent,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Devis sous 24h
          </div>
        </div>
      </div>
    </div>
  );
}

/* Bandeau slogan partagé entre les 3 volets intérieurs */
function InsideHeaderBanner() {
  return (
    <div
      data-testid="brochure-inside-banner"
      className="absolute top-0 left-0 right-0 z-10"
    >
      {/* Bandeau de couleurs (élargi) */}
      <div className="flex" style={{ height: "5mm" }}>
        <div className="flex-1" style={{ backgroundColor: "#8b5cf6" }} />
        <div className="flex-1" style={{ backgroundColor: "#ea580c" }} />
        <div className="flex-1" style={{ backgroundColor: "#0ea5e9" }} />
      </div>
      {/* Slogan */}
      <div
        className="relative flex items-center justify-center text-center"
        style={{
          height: "12mm",
          background: "linear-gradient(180deg, #050505 0%, rgba(10,10,10,0) 100%)",
        }}
      >
        <div className="flex items-baseline gap-[3mm]">
          <span
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "5.5mm",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "#ffffff",
            }}
          >
            Trois expertises.
          </span>
          <span
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "5.5mm",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "#9ca3af",
            }}
          >
            Une même exigence.
          </span>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   EXTÉRIEUR
   In roll fold (back side), reading order from left to right:
     [ PANEL LEFT (inside back) ] [ PANEL MIDDLE (back cover) ] [ PANEL RIGHT (front cover) ]
   But because the brochure is printed double-sided, the OUTSIDE
   is laid out flipped horizontally relative to the inside.
   For SIMPLICITY (and printer-friendliness), we lay out the outside
   from LEFT to RIGHT as: [BACK COVER] [TUCK PANEL] [FRONT COVER]
   ========================================================================= */
function PanelBackCover({ width }) {
  return (
    <div
      data-testid="flyer-back-cover"
      className="relative h-full bg-[#0a0a0a] text-white overflow-hidden"
      style={{ width: `${width}mm` }}
    >
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(70% 50% at 100% 100%, rgba(255,255,255,0.05), transparent 70%)" }}
      />
      <div className="absolute bottom-0 left-0 right-0 h-[3mm] flex">
        <div className="flex-1" style={{ backgroundColor: "#8b5cf6" }} />
        <div className="flex-1" style={{ backgroundColor: "#ea580c" }} />
        <div className="flex-1" style={{ backgroundColor: "#0ea5e9" }} />
      </div>

      <div className="relative h-full flex flex-col" style={{ padding: "16mm 9mm" }}>
        <div className="text-center">
          <div style={{ fontSize: "2.4mm", letterSpacing: "0.3em", color: "#9ca3af", textTransform: "uppercase" }}>
            Nous contacter
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "10mm",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1,
              marginTop: "2.5mm",
            }}
          >
            Parlons de<br />votre projet.
          </h2>
        </div>

        <div className="mt-[12mm] space-y-[6mm]">
          <div className="flex items-start" style={{ gap: "3.5mm" }}>
            <Phone size={18} style={{ color: "#fff", marginTop: "0.5mm", flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: "5mm", fontWeight: 700, letterSpacing: "0.02em" }}>05 32 18 90 44</div>
              <div className="flex items-center" style={{ gap: "1.8mm", fontSize: "2.5mm", color: "#9ca3af", marginTop: "1mm" }}>
                <MessageCircle size={10} style={{ color: "#22c55e" }} />
                <span>Disponible sur WhatsApp</span>
              </div>
              <div style={{ fontSize: "2.3mm", color: "#777", marginTop: "1.5mm", lineHeight: 1.5 }}>
                Lun–Ven 8h00 → 18h00<br />
                WhatsApp 24h/24
              </div>
            </div>
          </div>

          <div className="flex items-start" style={{ gap: "3.5mm" }}>
            <Globe size={18} style={{ color: "#fff", marginTop: "0.5mm", flexShrink: 0 }} />
            <div style={{ fontSize: "4mm", fontWeight: 600, letterSpacing: "0.02em" }}>groupeequinoxe.fr</div>
          </div>

          <div className="flex items-start" style={{ gap: "3.5mm" }}>
            <MapPin size={18} style={{ color: "#fff", marginTop: "0.5mm", flexShrink: 0 }} />
            <div style={{ fontSize: "3.2mm", lineHeight: 1.5, color: "#e5e5e5" }}>
              46090 FRANCOULÈS<br />
              46250 CAZALS
            </div>
          </div>
        </div>

        <div
          className="mt-auto text-center flex flex-wrap items-center justify-center"
          style={{ fontSize: "2mm", letterSpacing: "0.15em", textTransform: "uppercase", gap: "1.8mm" }}
        >
          <span style={{ color: "#8b5cf6", fontWeight: 700 }}>Equinoxe Studio</span>
          <span style={{ color: "#555" }}>·</span>
          <span style={{ color: "#ea580c", fontWeight: 700 }}>Equinoxe Sécurité</span>
          <span style={{ color: "#555" }}>·</span>
          <span style={{ color: "#0ea5e9", fontWeight: 700 }}>Equinoxe Propreté</span>
        </div>
      </div>
    </div>
  );
}

function PanelTuck({ width }) {
  return (
    <div
      data-testid="flyer-tuck"
      className="relative h-full bg-[#070707] text-white overflow-hidden"
      style={{ width: `${width}mm` }}
    >
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(60% 60% at 50% 50%, rgba(255,255,255,0.04), transparent 80%)" }}
      />
      <div className="relative h-full flex flex-col items-center justify-center" style={{ padding: "16mm 8mm" }}>
        <div style={{ fontSize: "2.4mm", letterSpacing: "0.3em", color: "#9ca3af", textTransform: "uppercase", marginBottom: "6mm" }}>
          Un groupe
        </div>
        <h2
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "10.5mm",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1,
            textAlign: "center",
          }}
        >
          Trois<br />expertises.<br />
          <span style={{ color: "#9ca3af" }}>Une même<br />exigence.</span>
        </h2>

        <p style={{ fontSize: "3.4mm", lineHeight: 1.55, color: "#e5e5e5", marginTop: "10mm", textAlign: "center", fontWeight: 400 }}>
          Conseil aux entreprises, sécurité d'événements et de locaux,
          entretien et propreté de vos espaces.
        </p>

        <div
          className="flex flex-wrap items-center justify-center mt-auto"
          style={{ gap: "1.8mm", fontSize: "2mm", letterSpacing: "0.12em" }}
        >
          <span style={{ color: "#8b5cf6", fontWeight: 700 }}>EQUINOXE STUDIO</span>
          <span style={{ color: "#555" }}>·</span>
          <span style={{ color: "#ea580c", fontWeight: 700 }}>EQUINOXE SÉCURITÉ</span>
          <span style={{ color: "#555" }}>·</span>
          <span style={{ color: "#0ea5e9", fontWeight: 700 }}>EQUINOXE PROPRETÉ</span>
        </div>
      </div>
    </div>
  );
}

function PanelFrontCover({ width }) {
  const VERSO_LOGO = "https://customer-assets.emergentagent.com/job_enterprise-hub-357/artifacts/6sv95ykm_Equinoxe_Groupe_noir-resize.png";
  return (
    <div
      data-testid="flyer-front-cover"
      className="relative h-full bg-black text-white overflow-hidden"
      style={{ width: `${width}mm` }}
    >
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(80% 60% at 50% 0%, rgba(255,255,255,0.08), transparent 70%)" }}
      />
      <div className="absolute top-0 left-0 right-0 flex" style={{ height: "5mm" }}>
        <div className="flex-1" style={{ backgroundColor: "#8b5cf6" }} />
        <div className="flex-1" style={{ backgroundColor: "#ea580c" }} />
        <div className="flex-1" style={{ backgroundColor: "#0ea5e9" }} />
      </div>

      <div className="relative h-full flex flex-col items-center justify-center text-center" style={{ padding: "16mm 6mm 14mm 6mm" }}>
        <img
          src={VERSO_LOGO}
          alt="Equinoxe Groupe"
          style={{ width: "78mm", height: "78mm", objectFit: "contain" }}
        />

        <div
          style={{
            marginTop: "8mm",
            fontSize: "3.2mm",
            lineHeight: 1.5,
            color: "#d4d4d4",
            maxWidth: "82mm",
            fontWeight: 500,
          }}
        >
          Un partenaire complet
          <br />pour vos projets pros & privés.
        </div>

        <div
          className="mt-auto"
          style={{
            fontSize: "3mm",
            color: "#ffffff",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          groupeequinoxe.fr
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   PAGE
   ========================================================================= */
export default function Brochure() {
  const { user, ready } = useAuth();
  if (ready === false) return null;
  if (ready && !user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-brochure">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-white mb-6 print:hidden"
          data-testid="brochure-back-admin"
        >
          <ArrowLeft size={14} /> Retour au tableau de bord
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 print:hidden">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Communication</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
              Dépliant 3 volets — Groupe Equinoxe
            </h1>
            <p className="mt-3 text-zinc-400 text-sm max-w-2xl">
              Format A4 horizontal (297 × 210 mm), pliage roulé (roll fold). Le panneau de droite
              est légèrement plus étroit pour s'imbriquer proprement lors du pliage.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => window.print()}
              data-testid="print-brochure"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-black font-semibold hover:bg-zinc-200"
            >
              <Printer size={16} /> Imprimer / PDF
            </button>
          </div>
        </div>

        {/* === INTÉRIEUR === */}
        <div className="mt-14">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4 print:hidden">
            Intérieur (les 3 pôles, lecture après dépliage)
          </div>
          <div className="brochure-shadow inline-block">
            <div
              className="relative flex bg-black"
              style={{ width: `${TOTAL_W}mm`, height: `${PAGE_H}mm` }}
              data-testid="brochure-inside"
            >
              <InsideHeaderBanner />
              <PanelPole branch={BRANCHES.studio} width={PANEL_LEFT} />
              <PanelPole branch={BRANCHES.securite} width={PANEL_MID} />
              <PanelPole branch={BRANCHES.proprete} width={PANEL_RIGHT} />

              {/* Fold guides */}
              <PanelDivider left={PANEL_LEFT} />
              <PanelDivider left={PANEL_LEFT + PANEL_MID} />
            </div>
          </div>
        </div>

        {/* === EXTÉRIEUR === */}
        <div className="mt-14 page-break-before">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4 print:hidden">
            Extérieur (couvertures et dos)
          </div>
          <div className="brochure-shadow inline-block">
            <div
              className="relative flex bg-black"
              style={{ width: `${TOTAL_W}mm`, height: `${PAGE_H}mm` }}
              data-testid="brochure-outside"
            >
              {/* Ordre extérieur, lecture gauche→droite quand recto retourné :
                  [ DOS DE COUVERTURE / VOLET INTÉRIEUR ] [ COUVERTURE ARRIÈRE ] [ COUVERTURE AVANT ] */}
              <PanelTuck width={PANEL_LEFT} />
              <PanelBackCover width={PANEL_MID} />
              <PanelFrontCover width={PANEL_RIGHT} />

              <PanelDivider left={PANEL_LEFT} />
              <PanelDivider left={PANEL_LEFT + PANEL_MID} />
            </div>
          </div>
        </div>

        {/* === SPECS === */}
        <div
          className="mt-16 rounded-xl border border-white/10 bg-[#0e0e0e] p-8 print:hidden"
          data-testid="brochure-specs"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">
            Spécifications pour l'imprimeur
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
            <Spec label="Format ouvert" value="297 × 210 mm (A4 horizontal)" />
            <Spec label="Format fermé" value="≈ 100 × 210 mm" />
            <Spec label="Pliage" value="3 volets, 2 plis roulés (roll fold)" />
            <Spec label="Panneaux" value="101 mm · 100 mm · 96 mm" />
            <Spec label="Recto-verso" value="Oui" />
            <Spec label="Quadrichromie" value="CMJN" />
            <Spec label="Fond perdu" value="+3 mm sur tous les bords" />
            <Spec label="Résolution" value="300 dpi minimum" />
            <Spec label="Papier suggéré" value="Couché mat 170 g/m² ou Soft-touch" />
            <Spec label="Finition" value="Mat (lisibilité optimale sur fond noir)" />
            <Spec label="Couleurs accents" value="#8b5cf6 · #ea580c · #0ea5e9" />
            <Spec label="Polices" value="Space Grotesk · Manrope" />
          </div>
          <div className="mt-6 text-xs text-zinc-500 leading-relaxed">
            <strong className="text-zinc-300">Sens de pliage</strong> : pli roulé — le volet droit
            (96 mm) se rabat vers l'intérieur, puis le volet gauche (101 mm) vient par-dessus.
            Le panneau "couverture avant" doit être à droite sur la maquette extérieure pour
            apparaître au premier plan une fois plié.
          </div>
        </div>
      </div>

      <style>{`
        .brochure-shadow {
          box-shadow: 0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04);
          border-radius: 1mm;
          overflow: hidden;
        }
        @media print {
          @page { size: 297mm 210mm; margin: 0; }
          html, body { background: white !important; }
          nav, header, footer { display: none !important; }
          .brochure-shadow { box-shadow: none !important; border-radius: 0; }
          .page-break-before { page-break-before: always; break-before: page; }
        }
      `}</style>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="mt-1 text-white">{value}</div>
    </div>
  );
}
